// /invite/{token}: the invitation is read first, before any sign-in. One state, one headline, one action.
// Signed out, with native sign-in: the invited email is fixed; a person who already has a login enters their
// password, a new one gives a name and a password and confirms the email with a code, and no company is asked
// (the invite names the workspace). Signed out, with Microsoft's pages: Continue starts Microsoft sign-in with the
// invited email filled in. Signed in as another account → Switch account; signed in as the invited email → it
// joins on its own and opens the workspace, where a welcome toast greets them; an invite that can't be used says
// so and who to ask.
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import type { SignInCodeRequiredState, SignInPasswordRequiredState, SignUpCodeRequiredState } from "@azure/msal-browser/custom-auth";
import { api, ApiError } from "../api/client";
import type { PortalUser, Role } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { advanceSignUp, isExpired, isLocked, passwordHint, passwordRefused, signInScopes, wrong, type NativeClient, type SignUpOutcome } from "../auth/native";
import { rememberTenant, useOptionalSession } from "../auth/SessionProvider";
import { getConfig } from "../config";
import { AuthActions, AuthBadge, AuthFrame, AuthHeading, authNote, authText, authTitle, CodeInput, LockedEmail, PasswordInput, TextButton, useCountdown } from "../shared/AuthFrame";
import { Button, Icon, Input } from "../ui";

/** The workspace an invite just joined; the shell shows the welcome toast once and forgets it. */
export const WELCOME_KEY = "av-welcome";

interface Look { state: "open" | "used" | "expired"; email: string | null; role: Role | null; company: string | null; invitedBy: string | null; invitedAt: string; expiresAt: string }

type Lookup = { kind: "loading" } | { kind: "unknown" } | { kind: "failed"; message: string } | { kind: "found"; look: Look };

type Accept =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "gone" }
  | { kind: "other" }
  | { kind: "conflict"; tenantId: string | null }
  | { kind: "error"; message: string };

const longDate = (value: string) => new Date(value).toLocaleDateString(undefined, { month: "long", day: "numeric" });

export function InvitePage() {
  const { token = "" } = useParams();
  const auth = useAuth();
  const session = useOptionalSession();
  const [lookup, setLookup] = useState<Lookup>({ kind: "loading" });
  const [accept, setAccept] = useState<Accept>({ kind: "idle" });
  const tried = useRef(false);

  // A plain fetch: the route needs no sign-in, and this page can render before the API client is set up.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${getConfig().apiUrl}/api/v1/invitations/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (response.status === 404) setLookup({ kind: "unknown" });
        else if (!response.ok) setLookup({ kind: "failed", message: `The API answered ${response.status}.` });
        else setLookup({ kind: "found", look: (await response.json()) as Look });
      } catch (error) {
        if (!cancelled) setLookup({ kind: "failed", message: error instanceof Error ? error.message : String(error) });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const look = lookup.kind === "found" ? lookup.look : null;
  const open = look?.state === "open";
  const signedIn = !!auth.account;
  const noEntra = auth.mode === "staff-key";
  const email = auth.account?.email ?? auth.account?.name ?? "";
  const matches = !!look && (!look.email || look.email.toLowerCase() === email.toLowerCase());
  const company = look?.company ?? "AutoVouch";
  const inviter = look?.invitedBy && look.invitedBy !== "sign-up" ? look.invitedBy : null;

  // Into the workspace: it becomes the one this browser session opens, and the shell says welcome.
  const openWorkspace = (tenantId: string | null, welcome: boolean) => {
    rememberTenant(tenantId);
    if (welcome) sessionStorage.setItem(WELCOME_KEY, company);
    window.location.assign("/");
  };

  const join = useCallback(async () => {
    setAccept({ kind: "busy" });
    try {
      const result = await api<{ user: PortalUser }>(`/invitations/${encodeURIComponent(token)}/accept`, { method: "POST", noTenant: true });
      rememberTenant(result.user.tenantId);
      sessionStorage.setItem(WELCOME_KEY, look?.company ?? "AutoVouch");
      window.location.assign("/");
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) setAccept({ kind: "gone" });
      else if (error instanceof ApiError && error.status === 403) setAccept({ kind: "other" });
      else if (error instanceof ApiError && error.status === 409) setAccept({ kind: "conflict", tenantId: typeof error.problem.tenantId === "string" ? error.problem.tenantId : null });
      else setAccept({ kind: "error", message: error instanceof ApiError ? error.problem.title ?? error.message : error instanceof Error ? error.message : String(error) });
    }
  }, [token, look?.company]);

  // Signed in as the invited person: join without asking, once.
  useEffect(() => {
    if (!open || !signedIn || !matches || noEntra || tried.current) return;
    tried.current = true;
    void join();
  }, [open, signedIn, matches, noEntra, join]);

  const expires = look && open ? `Invite expires ${longDate(look.expiresAt)}` : null;
  const signInElsewhere = () => window.location.assign("/");

  // An invite that can't be used: expired, used, unknown, or one the server turned away on accept.
  const ended = (title: string, text: string) => (
    <AuthFrame width={440}>
      <AuthBadge tone="warn" icon="clock" />
      <AuthHeading title={title} text={text} />
      <Button size="lg" variant="outline" onClick={signInElsewhere} trailingIcon={<Icon name="arrow-right" size={18} />} style={{ alignSelf: "flex-start" }}>
        {session ? "Open AutoVouch" : "Already have access? Sign in"}
      </Button>
    </AuthFrame>
  );
  const askFor = inviter ? `Ask ${inviter} to send you a new one.` : "Ask whoever invited you to send a new one.";

  if (lookup.kind === "loading") return <AuthFrame />;
  if (lookup.kind === "failed") {
    return (
      <AuthFrame width={440}>
        <AuthBadge tone="bad" icon="circle-alert" />
        <AuthHeading title="Couldn’t open this invite" text={lookup.message} />
        <Button size="lg" variant="outline" onClick={() => window.location.reload()} style={{ alignSelf: "flex-start" }}>Try again</Button>
      </AuthFrame>
    );
  }
  if (lookup.kind === "unknown") return ended("This invite isn’t valid", askFor);
  if (look!.state === "expired") return ended("This invite has expired", askFor);
  if (look!.state === "used" || accept.kind === "gone") return ended("This invite has already been used", session ? "Open AutoVouch to carry on." : askFor);

  if (accept.kind === "conflict") {
    return (
      <AuthFrame width={440}>
        <AuthBadge tone="info" icon="users" />
        <AuthHeading title={`You’re already in ${company}`} text="This login belongs to it under another email." />
        <AuthActions>
          <Button size="lg" onClick={() => openWorkspace(accept.tenantId, false)} trailingIcon={<Icon name="arrow-right" size={18} />}>Open {company}</Button>
          <Button size="lg" variant="ghost" leadingIcon={<Icon name="repeat" size={18} />} onClick={auth.switchAccount}>Switch account</Button>
        </AuthActions>
      </AuthFrame>
    );
  }

  // Signed in as someone else.
  if (signedIn && (!matches || accept.kind === "other")) {
    return (
      <AuthFrame width={440} footer={expires}>
        <AuthHeading eyebrow={`Invite to ${company}`} title="You’re signed in as someone else" />
        <div style={{ borderRadius: "var(--radius-lg)", border: "1px solid var(--border-subtle)", background: "var(--surface-card)", overflow: "hidden", boxShadow: "var(--shadow-sm)" }}>
          <Identity tone="ok" label="This invite is for" email={look!.email ?? ""} />
          <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <Identity tone="bad" label="You’re signed in as" email={email} />
          </div>
        </div>
        <AuthActions column>
          <Button size="lg" fullWidth leadingIcon={<Icon name="repeat" size={18} />} onClick={() => (auth.native ? auth.switchAccount() : auth.signIn({ loginHint: look!.email ?? undefined, prompt: "login" }))}>Switch account</Button>
          <p style={authNote}>Wrong address? Ask {inviter ?? "whoever invited you"} to invite {email} instead.</p>
        </AuthActions>
      </AuthFrame>
    );
  }

  // Signed in as the invited person: joining, then straight into the workspace.
  if (signedIn) {
    if (accept.kind === "error") {
      return (
        <AuthFrame width={440}>
          <AuthBadge tone="bad" icon="circle-alert" />
          <AuthHeading title={`Couldn’t join ${company}`} text={accept.message} />
          <Button size="lg" onClick={() => void join()} style={{ alignSelf: "flex-start" }}>Try again</Button>
        </AuthFrame>
      );
    }
    return (
      <AuthFrame center width={440}>
        <span aria-hidden="true" style={{ width: 56, height: 56, borderRadius: "50%", border: "4px solid var(--av-blue-100)", borderTopColor: "var(--action-primary)", boxSizing: "border-box", animation: "av-spin .9s linear infinite" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h1 style={{ ...authTitle, fontSize: 34 }}>Joining {company}…</h1>
          <span style={{ font: "var(--type-body)", color: "var(--text-muted)" }}>{email}</span>
        </div>
      </AuthFrame>
    );
  }

  const invitedBy = inviter && (
    <span style={{ display: "flex", alignItems: "center", gap: 10, font: "600 15px var(--font-body)", color: "var(--text-muted)" }}>
      <span aria-hidden="true" style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--gradient-fresh)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", font: "800 13px var(--font-display)" }}>{inviter.slice(0, 1).toUpperCase()}</span>
      {inviter} invited you
    </span>
  );

  // Not signed in, on the portal's own sign-in screens.
  if (auth.native && look!.email) {
    return <NativeInvite native={auth.native} scope={auth.auth.scope!} token={token} email={look!.email} company={company} invitedBy={invitedBy || null} footer={expires} />;
  }

  // Not signed in, with Microsoft's sign-in pages.
  return (
    <AuthFrame width={440} footer={expires}>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {invitedBy}
        <h1 style={authTitle}>Join {company} on AutoVouch</h1>
      </div>
      {look!.email && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", borderRadius: "var(--radius-lg)", background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)" }}>
          <span aria-hidden="true" style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--av-blue-100)", color: "var(--text-link)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Icon name="mail" size={20} /></span>
          <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <span style={{ font: "600 12px var(--font-body)", color: "var(--text-muted)" }}>Your invite is for</span>
            <span style={{ font: "700 17px var(--font-body)", color: "var(--text-strong)", overflowWrap: "anywhere" }}>{look!.email}</span>
          </span>
        </div>
      )}
      {noEntra ? (
        <p style={{ ...authText, color: "var(--text-muted)" }}>Microsoft sign-in isn’t set up on this environment, so this invite can’t be accepted here.</p>
      ) : auth.native ? (
        <Button size="lg" fullWidth onClick={signInElsewhere} trailingIcon={<Icon name="arrow-right" size={18} />}>Sign in to join</Button>
      ) : (
        <AuthActions column>
          <Button size="lg" fullWidth onClick={() => auth.signIn({ loginHint: look!.email ?? undefined })} trailingIcon={<Icon name="arrow-right" size={18} />}>Continue</Button>
          <p style={authNote}>New to AutoVouch? You’ll create your login next.<br />Already have one? You’ll sign in.</p>
        </AuthActions>
      )}
    </AuthFrame>
  );
}

type Step =
  | { kind: "checking" }
  | { kind: "existing"; state: SignInPasswordRequiredState }
  | { kind: "code"; state: SignInCodeRequiredState }
  | { kind: "new" }
  | { kind: "confirm"; state: SignUpCodeRequiredState }
  | { kind: "locked" }
  | { kind: "failed"; message: string };

/** Signed in: back to this page, which then joins on its own. */
const backHere = () => window.location.reload();

/**
 * The invited email is fixed. Asking Entra to start a sign-in for it says whether it already has a login: a
 * password is wanted (sign in to join), or there's no such user (create the login here).
 */
function NativeInvite({ native, scope, token, email, company, invitedBy, footer }: { native: NativeClient; scope: string; token: string; email: string; company: string; invitedBy: React.ReactNode; footer: string | null }) {
  const [step, setStep] = useState<Step>({ kind: "checking" });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [problem, setProblem] = useState<Partial<Record<"name" | "password" | "form", string>>>({});
  const [busy, setBusy] = useState(false);
  const resend = useCountdown(30);
  const started = useRef(false);
  const resetLink = `/reset-password?email=${encodeURIComponent(email)}&next=${encodeURIComponent(`/invite/${token}`)}`;

  const check = useCallback(async () => {
    setStep({ kind: "checking" });
    const result = await native.signIn({ username: email, scopes: signInScopes(scope) });
    const error = result.error;
    if (result.isPasswordRequired()) setStep({ kind: "existing", state: result.state });
    else if (result.isCodeRequired()) { resend.restart(); setStep({ kind: "code", state: result.state }); }
    else if (result.isCompleted()) backHere();
    else if (error?.isUserNotFound()) setStep({ kind: "new" });
    else if (isLocked(error)) setStep({ kind: "locked" });
    else setStep({ kind: "failed", message: wrong("invite email step", error) });
    // resend is a fresh object each render; only the client, the email and the scope start a new check.
  }, [native, email, scope]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void check();
  }, [check]);

  const signInWithPassword = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (step.kind !== "existing") return;
    if (!password) return setProblem({ password: "Enter your password." });
    setBusy(true);
    setProblem({});
    try {
      const result = await step.state.submitPassword(password);
      const error = result.error;
      if (result.isCompleted()) return backHere();
      if (isLocked(error)) return setStep({ kind: "locked" });
      if (isExpired(error)) return void check();
      setProblem({ password: error?.isInvalidPassword() ? "That password doesn’t match. Try again, or reset it." : wrong("invite password", error) });
    } finally {
      setBusy(false);
    }
  };

  const follow = async (outcome: SignUpOutcome) => {
    if (outcome.kind === "done") {
      const signedIn = await outcome.state.signIn({ scopes: signInScopes(scope) });
      return signedIn.isCompleted() ? backHere() : void check();
    }
    if (outcome.kind === "code") {
      setCode("");
      resend.restart(outcome.state.getCodeResendInterval() || 30);
      return setStep({ kind: "confirm", state: outcome.state });
    }
    const error = outcome.error;
    if (error?.isUserAlreadyExists?.()) return void check();
    if (error?.isInvalidPassword?.()) { setStep({ kind: "new" }); return setProblem({ password: passwordRefused }); }
    if (error?.isInvalidCode?.()) return setProblem({ form: "That code didn’t work. It may have expired: send a new one." });
    if (isExpired(error)) { setStep({ kind: "new" }); return setProblem({ form: "That took a while, so we started over." }); }
    setProblem({ form: wrong("invite sign-up", error) });
  };

  const values = () => ({ password, displayName: name.trim(), company });

  const createLogin = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const found = { name: name.trim() ? undefined : "Tell us your name.", password: password ? undefined : "Create a password." };
    if (found.name || found.password) return setProblem(found);
    setBusy(true);
    setProblem({});
    try {
      await follow(await advanceSignUp(await native.signUp({ username: email, password, attributes: { displayName: name.trim() } }), values()));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (value: string) => {
    if (step.kind !== "confirm") return;
    setBusy(true);
    setProblem({});
    try {
      await follow(await advanceSignUp(await step.state.submitCode(value), values()));
    } finally {
      setBusy(false);
    }
  };

  const signInWithCode = async (value: string) => {
    if (step.kind !== "code") return;
    setBusy(true);
    setProblem({});
    try {
      const result = await step.state.submitCode(value);
      const error = result.error;
      if (result.isCompleted()) return backHere();
      setProblem({ form: error?.isInvalidCode() ? "That code didn’t work. It may have expired: send a new one." : wrong("invite code", error) });
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    setProblem({});
    if (step.kind === "confirm") {
      const result = await step.state.resendCode();
      if (result.isCodeRequired()) { setCode(""); resend.restart(); setStep({ kind: "confirm", state: result.state }); return; }
    } else if (step.kind === "code") {
      const result = await step.state.resendCode();
      if (result.isCodeRequired()) { setCode(""); resend.restart(); setStep({ kind: "code", state: result.state }); return; }
    }
    setProblem({ form: wrong("invite resend code", undefined) });
  };

  const header = (text?: string) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {invitedBy}
      <h1 style={authTitle}>Join {company} on AutoVouch</h1>
      {text && <p style={authText}>{text}</p>}
    </div>
  );

  if (step.kind === "checking") {
    return (
      <AuthFrame width={440} footer={footer}>
        {header()}
        <LockedEmail email={email} />
        <span aria-label="Checking your invite" style={{ alignSelf: "center", width: 32, height: 32, borderRadius: "50%", border: "3px solid var(--av-blue-100)", borderTopColor: "var(--action-primary)", boxSizing: "border-box", animation: "av-spin .9s linear infinite" }} />
      </AuthFrame>
    );
  }

  if (step.kind === "failed") {
    return (
      <AuthFrame width={440} footer={footer}>
        <AuthBadge tone="bad" icon="circle-alert" />
        <AuthHeading title="Couldn’t open sign-in" text={step.message} />
        <Button size="lg" onClick={() => void check()} style={{ alignSelf: "flex-start" }}>Try again</Button>
      </AuthFrame>
    );
  }

  if (step.kind === "locked") {
    return (
      <AuthFrame width={440} footer={footer}>
        <AuthBadge tone="warn" icon="lock" />
        <AuthHeading title="Let’s take five" text="That’s a lot of tries in a row, so sign-in is paused for a few minutes. Can’t remember it? Reset it now." />
        <AuthActions column>
          <Button size="lg" fullWidth onClick={() => window.location.assign(resetLink)}>Reset password</Button>
          <p style={authNote}><TextButton onClick={() => void check()}>Try again</TextButton></p>
        </AuthActions>
      </AuthFrame>
    );
  }

  if (step.kind === "confirm" || step.kind === "code") {
    const length = step.state.getCodeLength() || 8;
    const submit = step.kind === "confirm" ? confirm : signInWithCode;
    return (
      <AuthFrame width={440} footer={footer}>
        <AuthHeading eyebrow={`Joining ${company}`} title={step.kind === "confirm" ? "One last step" : "Check your inbox"} text={<>Enter the code we sent to <b style={{ color: "var(--text-strong)" }}>{email}</b>{step.kind === "confirm" ? " to confirm it’s you." : "."}</>} />
        <CodeInput length={length} value={code} invalid={!!problem.form} onChange={(value) => { setCode(value); setProblem({}); if (value.length === length) void submit(value); }} />
        {problem.form && <p role="alert" style={{ ...authText, fontSize: 14, color: "var(--status-mismatch-fg)", marginTop: -14 }}>{problem.form}</p>}
        <AuthActions column>
          <Button size="lg" fullWidth loading={busy} disabled={code.length < length} onClick={() => void submit(code)}>Join {company}</Button>
          <p style={authNote}>{resend.left > 0 ? `Nothing yet? Resend in 0:${String(resend.left).padStart(2, "0")}` : <>Nothing yet? Check spam, or <TextButton onClick={() => void resendCode()}>resend the code</TextButton></>}</p>
        </AuthActions>
      </AuthFrame>
    );
  }

  if (step.kind === "existing") {
    return (
      <AuthFrame width={440} footer={footer}>
        {header("You already have a login. Sign in to join.")}
        <form noValidate onSubmit={signInWithPassword} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <LockedEmail email={email} />
          <PasswordInput label={<span style={{ display: "flex", justifyContent: "space-between", width: "100%" }}><span>Password</span><a href={resetLink} style={{ font: "700 14px var(--font-body)" }}>Forgot password?</a></span>} autoComplete="current-password" autoFocus value={password} onChange={(e) => { setPassword(e.target.value); setProblem({}); }} error={problem.password} />
          <Button type="submit" size="lg" fullWidth loading={busy}>Sign in and join</Button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame width={440} footer={footer}>
      {header()}
      <form noValidate onSubmit={createLogin} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <LockedEmail email={email} hint="Your invite is tied to this address." />
          <Input size="lg" label="Your name" autoComplete="name" autoFocus value={name} onChange={(e) => { setName(e.target.value); setProblem({}); }} error={problem.name} />
          <PasswordInput label="Create a password" autoComplete="new-password" value={password} onChange={(e) => { setPassword(e.target.value); setProblem({}); }} hint={passwordHint} error={problem.password} />
        </div>
        {problem.form && <p role="alert" style={{ ...authText, fontSize: 14, color: "var(--status-mismatch-fg)" }}>{problem.form}</p>}
        <Button type="submit" size="lg" fullWidth loading={busy}>Join {company}</Button>
      </form>
    </AuthFrame>
  );
}

function Identity({ tone, label, email }: { tone: "ok" | "bad"; label: string; email: string }) {
  const ok = tone === "ok";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 18px", background: ok ? "var(--surface-sunken)" : "transparent" }}>
      <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: "50%", background: ok ? "var(--status-matched-bg)" : "var(--status-mismatch-bg)", color: ok ? "var(--status-matched-fg)" : "var(--status-mismatch-fg)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
        <Icon name={ok ? "check" : "x"} size={18} stroke={2.6} />
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ font: "600 12px var(--font-body)", color: "var(--text-muted)" }}>{label}</span>
        <span style={{ font: `${ok ? 700 : 600} 17px var(--font-body)`, color: ok ? "var(--text-strong)" : "var(--av-gray-600)", overflowWrap: "anywhere" }}>{email}</span>
      </span>
    </div>
  );
}
